import { FadeUp } from '../primitives/FadeUp';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ACCENT, FONT_DISPLAY, FONT_MONO, SURFACE2, TEXT_PRIMARY, TEXT_SECONDARY } from '../../tokens';

interface FounderStripProps {
  onAbout: () => void;
}

/** Founder introduction — the attachment beat between problem and solution. */
export function FounderStrip({ onAbout }: FounderStripProps) {
  const isMobile = useIsMobile();

  return (
    <section
      id="founder"
      style={{ padding: isMobile ? '60px 20px' : '100px 64px', background: 'rgba(6,6,6,0.88)' }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <FadeUp>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              maxWidth: 760,
              margin: '0 auto',
              gap: isMobile ? 32 : 40,
            }}
          >
            <div style={{ position: 'relative' }}>
              <img
                src="/about-photo.jpg"
                alt="Brody Zwiers presenting at Claude Community Canberra"
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: 4,
                  border: `2px solid ${ACCENT}`,
                  boxShadow: `8px 8px 0 ${ACCENT}`,
                  display: 'block',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: -12,
                  right: -12,
                  background: ACCENT,
                  color: SURFACE2,
                  padding: '6px 14px',
                  borderRadius: 4,
                  fontFamily: FONT_MONO,
                  fontSize: '0.62rem',
                  letterSpacing: '0.1em',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  transform: 'rotate(-3deg)',
                }}
              >
                Canberra, AU
              </div>
            </div>

            <div>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  background: 'rgba(255,107,0,0.08)',
                  border: '1px solid rgba(255,107,0,0.25)',
                  color: ACCENT,
                  fontSize: '0.7rem',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  padding: '8px 18px',
                  borderRadius: 100,
                  marginBottom: 24,
                  fontFamily: FONT_MONO,
                  fontWeight: 500,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: ACCENT,
                    display: 'inline-block',
                    animation: 'pulse-dot 2s ease-in-out infinite',
                  }}
                />
                Content OS, built in Canberra
              </div>

              <h3
                style={{
                  fontFamily: FONT_DISPLAY,
                  fontSize: isMobile ? '1.8rem' : 'clamp(2rem, 2.8vw, 2.6rem)',
                  fontWeight: 400,
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                  color: TEXT_PRIMARY,
                  lineHeight: 1.05,
                  marginBottom: 16,
                }}
              >
                I got tired of watching good businesses lose to worse ones that just posted more often.
              </h3>

              <p
                style={{
                  color: TEXT_SECONDARY,
                  fontSize: '1.05rem',
                  lineHeight: 1.75,
                  marginBottom: 24,
                  maxWidth: 520,
                }}
              >
                So I built the system that does the posting, the follow-up and the review
                chasing, grounded in your own sales calls so it still sounds like you. No black boxes,
                no lock-in, and everything is yours if you leave.
              </p>

              <button
                type="button"
                onClick={onAbout}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: ACCENT,
                  fontFamily: FONT_MONO,
                  fontSize: '0.75rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: 0,
                }}
              >
                Read the full story →
              </button>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
